import { Service } from '@angular/core';
import {SureFlowLoopDetail} from '../model/sure-flow-loop-detail';
import {SureFlowUnitData} from '../model/sure-flow-unit-data';
import {UnitConversionUtils} from '../configurator/unit-conversion-utils';
import {StringUtils} from '../utils/string-utils';
import {SureFlowRatingUnit} from '../model/sure-flow-rating-unit';
import {SureFlowRatingCoilRequest} from '../model/sure-flow-rating-coil-request';

@Service()
export class SureFlowUnitConversionService {

  convertLoopDetailValues(input: SureFlowLoopDetail, isMetric: boolean): void {
    if (input) {
      input.coolingActualSensibleCapacity = this.convertCapacityIfPresent(input.coolingActualSensibleCapacity, isMetric);
      input.coolingActualTotalCapacity = this.convertCapacityIfPresent(input.coolingActualTotalCapacity, isMetric);
      input.coolingEnteringFluidTemperature = this.convertTemperatureIfPresent(input.coolingEnteringFluidTemperature, isMetric);
      input.coolingFluidVelocity = this.convertFluidVelocityIfPresent(input.coolingFluidVelocity, isMetric);
      input.coolingLeavingFluidTemperature = this.convertTemperatureIfPresent(input.coolingLeavingFluidTemperature, isMetric);
      input.coolingPeakFluidFlowRate = this.convertFlowRateIfPresent(input.coolingPeakFluidFlowRate, isMetric);
      input.coolingPeakFluidTemperatureChange = this.convertTemperatureChangeIfPresent(input.coolingPeakFluidTemperatureChange, isMetric);
      input.coolingPeakSensibleCapacity = this.convertCapacityIfPresent(input.coolingPeakSensibleCapacity, isMetric);
      input.coolingPeakTotalCapacity = this.convertCapacityIfPresent(input.coolingPeakTotalCapacity, isMetric);
      input.heatingActualTotalCapacity = this.convertCapacityIfPresent(input.heatingActualTotalCapacity, isMetric);
      input.heatingEnteringFluidTemperature = this.convertTemperatureIfPresent(input.heatingEnteringFluidTemperature, isMetric);
      input.heatingFluidVelocity = this.convertFluidVelocityIfPresent(input.heatingFluidVelocity, isMetric);
      input.heatingLeavingFluidTemperature = this.convertTemperatureIfPresent(input.heatingLeavingFluidTemperature, isMetric);
      input.heatingPeakFluidFlowRate = this.convertFlowRateIfPresent(input.heatingPeakFluidFlowRate, isMetric);
      input.heatingPeakFluidTemperatureChange = this.convertTemperatureChangeIfPresent(input.heatingPeakFluidTemperatureChange, isMetric);
      input.heatingPeakTotalCapacity = this.convertCapacityIfPresent(input.heatingPeakTotalCapacity, isMetric);
      input.metric = isMetric;
    }
  }

  private convertTemperatureChangeIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertTemperatureChange(input, isMetric);
  }

  private convertFlowRateIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertFlowRate(input, isMetric);
  }

  private convertFluidVelocityIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertFluidVelocity(input, isMetric);
  }

  private convertTemperatureIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertTemperatureValue(input, isMetric);
  }

  private convertCapacityIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertCapacityValue(input, isMetric);
  }

  convertUnitDataValues(unit: SureFlowUnitData, isMetric: boolean): void {
    if (unit) {
      unit.coolingTargetTotalLoad = this.convertCapacityIfPresent(unit.coolingTargetTotalLoad, isMetric);
      unit.coolingTargetSensibleLoad = this.convertCapacityIfPresent(unit.coolingTargetSensibleLoad, isMetric);
      unit.coolingTargetLatentLoad = this.convertCapacityIfPresent(unit.coolingTargetLatentLoad, isMetric);
      unit.coolingFluidFlowRate = this.convertFlowRateIfPresent(unit.coolingFluidFlowRate, isMetric);
      unit.coolingEnteringFluidTemperature = this.convertTemperatureIfPresent(unit.coolingEnteringFluidTemperature, isMetric);
      unit.heatingTargetTotalLoad = this.convertCapacityIfPresent(unit.heatingTargetTotalLoad, isMetric);
      unit.heatingFluidFlowRate = this.convertFlowRateIfPresent(unit.heatingFluidFlowRate, isMetric);
      unit.heatingEnteringFluidTemperature = this.convertTemperatureIfPresent(unit.heatingEnteringFluidTemperature, isMetric);
    }
  }

  convertRatingUnitValues(ratingUnit: SureFlowRatingUnit, isMetric: boolean): void {
    if (ratingUnit && ratingUnit.request) {
      const request = ratingUnit.request;
      request.metric = isMetric;
      request.airflow = this.convertAirflowIfPresent(request.airflow, isMetric);
      request.altitude = this.convertAltitudeIfPresent(request.altitude, isMetric);
      request.externalStaticPressure = this.convertStaticPressureIfPresent(request.externalStaticPressure, isMetric);
      request.totalStaticPressure = this.convertStaticPressureIfPresent(request.totalStaticPressure, isMetric);
      this.convertCoilRequestValues(request.primaryCoilRequest, isMetric);
      this.convertCoilRequestValues(request.secondaryCoilRequest, isMetric);
      this.convertCoilRequestValues(request.electricHeatRequest, isMetric);
    }
  }

  private convertStaticPressureIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertStaticPressure(input, isMetric);
  }

  private convertAltitudeIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertAltitudeValue(input, isMetric);
  }

  private convertAirflowIfPresent(input: number, isMetric: boolean): number {
    if (StringUtils.isEmptyNumericValue(input)) {
      return undefined!;
    }
    return +UnitConversionUtils.convertAirflow(input, isMetric);
  }

  private convertCoilRequestValues(coilRequest: SureFlowRatingCoilRequest, isMetric: boolean): void {
    if (coilRequest) {
      coilRequest.enteringDryBulb = this.convertTemperatureIfPresent(coilRequest.enteringDryBulb, isMetric);
      coilRequest.flowRate = this.convertFlowRateIfPresent(coilRequest.flowRate, isMetric);
      coilRequest.enteringFluidTemperature = this.convertTemperatureIfPresent(coilRequest.enteringFluidTemperature, isMetric);
      coilRequest.enteringWetBulb = this.convertTemperatureIfPresent(coilRequest.enteringWetBulb, isMetric);
      coilRequest.temperatureChange = this.convertTemperatureChangeIfPresent(coilRequest.temperatureChange, isMetric);
    }
  }

}
